import { useState } from 'react';
import { fold } from '../units';

export interface IUseSimilarNameGuardArgs {
  query: string;
  closeMatch: string | null;
  /** Sync/re-count stock dialogs and delivery edits never mint a name. */
  isNewMaterial: boolean;
  onUseExisting: (name: string) => void;
  onSave: () => void;
}

/**
 * The save-time similar-name guard (fuzzy-matching design 2026-08-31): state
 * machine only — the question itself is the shared `DiscardModal` with an
 * `altAction`. Never a hard block: Keep-as-new always wins the second click,
 * and the answer is keyed to the NAME so a re-picked material cannot inherit
 * a stale confirmation (the area-choice pattern).
 */
export function useSimilarNameGuard(args: IUseSimilarNameGuardArgs) {
  const [keptNewFor, setKeptNewFor] = useState<string | null>(null);
  const [asked, setAsked] = useState(false);
  // The answered name is compared FOLDED — the feature's own identity rule;
  // a case/width respelling of an accepted name is not a new question.
  const match = args.isNewMaterial && keptNewFor !== fold(args.query) ? args.closeMatch : null;
  return {
    match,
    open: asked && match != null,
    /** The Save button's real handler: ask once when suspicious, else save. */
    onSaveClick: () => {
      if (match != null) setAsked(true);
      else args.onSave();
    },
    /** Cancel/Esc/backdrop: back to the form, nothing changes. */
    dismiss: () => setAsked(false),
    useExisting: () => {
      if (match != null) args.onUseExisting(match);
      setAsked(false);
    },
    keepNew: () => {
      setKeptNewFor(fold(args.query));
      setAsked(false);
      args.onSave();
    },
  };
}
